import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Failover drill console (part 61).
 *
 * A drill is an assessment plus a runbook. This page starts a drill from the measured state of a
 * primary/standby pair, shows the promotion gate and the ordered steps, records step results and
 * records the post-promotion validation. Nothing here runs pg_ctl: the commands are a runbook for
 * the operator or scripts/failover-drill.sh.
 *
 * The page is not routed from App.tsx in this part; the API mount is also intentionally unmounted.
 * Both are one-line wiring changes reviewed with the first operator who needs the console.
 */

interface FailoverTarget {
  primary_host: string
  primary_port: number
  standby_host: string
  standby_port: number
  slot_name: string
  primary_data_dir?: string
  standby_data_dir?: string
}

interface FailoverCheck {
  id: string
  title: string
  status: 'pass' | 'warn' | 'fail'
  detail: string
  remediation: string
  blocking: boolean
}

interface FailoverSummary {
  total: number
  passed: number
  warned: number
  failed: number
}

interface FailoverGate {
  allow: boolean
  decision: 'allow' | 'deny'
  reason: string
  blockers: string[]
  warnings: string[]
  acknowledged: boolean
  requires_acknowledgement: boolean
}

interface FailoverPolicyItem {
  id: string
  title: string
  blocking: boolean
  rule: string
}

interface FailoverPolicyView {
  max_replication_lag_ms: number
  warn_replication_lag_ms: number
  max_replication_lag_bytes: number
  warn_replication_lag_bytes: number
  max_checkpoint_age_ms: number
  warn_checkpoint_age_ms: number
  max_slot_retained_bytes: number
  warn_slot_retained_bytes: number
  checks: FailoverPolicyItem[]
}

interface FailoverReport {
  observed_at: string
  checks: FailoverCheck[]
  summary: FailoverSummary
  gate: FailoverGate
  policy: FailoverPolicyView
}

interface FailoverUndo {
  id: string
  position: number
  phase: string
  title: string
  detail: string
  command: string
  timeout_seconds: number
  irreversible: boolean
  requires_approval: boolean
}

interface FailoverPlanStep {
  id: string
  position: number
  phase: string
  title: string
  detail: string
  command: string
  timeout_seconds: number
  irreversible: boolean
  requires_approval: boolean
  undo?: FailoverUndo
}

interface FailoverPlan {
  target: FailoverTarget
  generated_at: string
  steps: FailoverPlanStep[]
}

interface FailoverDrill {
  id: string
  tenant_id: string
  mode: 'dry_run' | 'execute'
  status: 'planned' | 'running' | 'validated' | 'failed' | 'aborted'
  target: FailoverTarget
  decision: 'allow' | 'deny'
  decision_reason: string
  acknowledged_warnings: boolean
  checks_total: number
  checks_passed: number
  checks_warned: number
  checks_failed: number
  blocking_checks: string[]
  warning_checks: string[]
  checks: FailoverCheck[]
  replication_lag_ms: number
  replication_lag_bytes: number
  slot_retained_bytes: number
  checkpoint_age_ms: number
  primary_timeline: number
  standby_timeline: number
  promoted_timeline?: number
  timeline_bumped: boolean
  writes_verified: boolean
  old_primary_demoted: boolean
  validation_ok: boolean
  validated: boolean
  validation_reason: string
  steps_planned: number
  started_by?: string
  started_by_role: string
  started_at: string
  completed_at?: string
}

interface FailoverStep {
  id: string
  drill_id: string
  step_id: string
  position: number
  direction: 'forward' | 'reverse'
  title: string
  status: 'done' | 'failed' | 'skipped' | 'rolled_back'
  detail: string
  attempt: number
  recorded_by?: string
  recorded_by_role: string
  recorded_at: string
}

interface FailoverDrillDetail {
  drill: FailoverDrill
  steps: FailoverStep[]
  plan: FailoverPlan
}

interface FailoverReadiness {
  ready: boolean
  note: string
  last_drill?: FailoverDrill
  checks: FailoverCheck[]
  steps_recorded: number
}

interface StartDrillResult {
  drill: FailoverDrill
  plan: FailoverPlan
  report: FailoverReport
}

interface ValidationCheckResult {
  ok: boolean
  summary: FailoverSummary
  checks: FailoverCheck[]
  timeline: {
    previous_timeline: number
    promoted_timeline: number
    expected_timeline: number
    bumped: boolean
    status: 'pass' | 'warn' | 'fail'
    detail: string
  }
  reason: string
  observed_at: string
}

interface ValidateDrillResult {
  drill: FailoverDrill
  validation: ValidationCheckResult
}

interface DrillForm {
  mode: 'dry_run' | 'execute'
  primary_host: string
  primary_port: number
  standby_host: string
  standby_port: number
  slot_name: string
  primary_data_dir: string
  standby_data_dir: string
  acknowledge_warnings: boolean
  primary_in_recovery: boolean
  standby_in_recovery: boolean
  standby_accepting: boolean
  replication_state: string
  replication_lag_ms: number
  replication_lag_bytes: number
  slot_exists: boolean
  slot_active: boolean
  slot_retained_bytes: number
  checkpoint_age_ms: number
  primary_timeline: number
  standby_timeline: number
}

const READINESS_KEY = ['failover-readiness']
const DRILLS_KEY = ['failover-drills']

function defaultForm(): DrillForm {
  return {
    mode: 'dry_run',
    primary_host: '127.0.0.1',
    primary_port: 55442,
    standby_host: '127.0.0.1',
    standby_port: 55443,
    slot_name: 'failover_slot',
    primary_data_dir: '.data/failover-primary',
    standby_data_dir: '.data/failover-standby',
    acknowledge_warnings: false,
    primary_in_recovery: false,
    standby_in_recovery: true,
    standby_accepting: true,
    replication_state: 'streaming',
    replication_lag_ms: 120,
    replication_lag_bytes: 1048576,
    slot_exists: true,
    slot_active: true,
    slot_retained_bytes: 16777216,
    checkpoint_age_ms: 45000,
    primary_timeline: 1,
    standby_timeline: 1,
  }
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function formatDuration(ms: number): string {
  if (ms <= 0) return '0 มิลลิวินาที'
  if (ms < 1000) return `${ms} มิลลิวินาที`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} วินาที`
  return `${(ms / 60000).toFixed(1)} นาที`
}

function checkClass(status: FailoverCheck['status']): string {
  if (status === 'pass') return 'badge badge-closed'
  if (status === 'warn') return 'badge badge-reviewing'
  return 'badge badge-received'
}

function checkLabel(status: FailoverCheck['status']): string {
  if (status === 'pass') return 'ผ่าน'
  if (status === 'warn') return 'ควรระวัง'
  return 'ไม่ผ่าน'
}

function statusClass(status: FailoverDrill['status']): string {
  if (status === 'validated') return 'badge badge-closed'
  if (status === 'failed') return 'badge badge-received'
  if (status === 'running') return 'badge badge-reviewing'
  return 'chip'
}

function statusLabel(status: FailoverDrill['status']): string {
  switch (status) {
    case 'planned':
      return 'วางแผนแล้ว'
    case 'running':
      return 'กำลังซ้อม'
    case 'validated':
      return 'ผ่านการตรวจสอบ'
    case 'failed':
      return 'ไม่ผ่าน'
    case 'aborted':
      return 'ยกเลิก'
  }
  return status
}

function modeLabel(mode: FailoverDrill['mode']): string {
  return mode === 'execute' ? 'ดำเนินการจริง' : 'ซ้อมแผน (dry run)'
}

function stepStatusLabel(status: FailoverStep['status']): string {
  switch (status) {
    case 'done':
      return 'สำเร็จ'
    case 'failed':
      return 'ล้มเหลว'
    case 'skipped':
      return 'ข้าม'
    case 'rolled_back':
      return 'ย้อนกลับแล้ว'
  }
  return status
}

function phaseLabel(phase: string): string {
  switch (phase) {
    case 'preflight':
      return 'ตรวจก่อนเริ่ม'
    case 'fence':
      return 'กันการเขียน'
    case 'promote':
      return 'เลื่อนขั้น'
    case 'validate':
      return 'ตรวจสอบ'
    case 'repoint':
      return 'สลับเส้นทาง'
    case 'rebuild':
      return 'สร้างใหม่'
  }
  return phase
}

export function Failover() {
  const qc = useQueryClient()
  const [form, setForm] = useState<DrillForm>(defaultForm)
  const [notice, setNotice] = useState<string | null>(null)
  const [startResult, setStartResult] = useState<StartDrillResult | null>(null)
  const [selectedID, setSelectedID] = useState('')
  const [stepChoice, setStepChoice] = useState('')
  const [stepStatus, setStepStatus] = useState<FailoverStep['status']>('done')
  const [stepDetail, setStepDetail] = useState('')
  const [validation, setValidation] = useState({
    promoted_host: '127.0.0.1',
    promoted_port: 55443,
    promoted_in_recovery: false,
    writes_accepted: true,
    probe_value: 'promotion-ok',
    timeline: 2,
    old_primary_in_recovery: true,
    old_primary_streaming: true,
  })

  const readinessQ = useQuery({
    queryKey: READINESS_KEY,
    queryFn: () => api<FailoverReadiness>('/v1/failover/readiness'),
    staleTime: 15_000,
  })

  const drillsQ = useQuery({
    queryKey: DRILLS_KEY,
    queryFn: () => api<FailoverDrill[]>('/v1/failover/drills?limit=50'),
    staleTime: 15_000,
  })

  const detailQ = useQuery({
    queryKey: ['failover-drill', selectedID],
    queryFn: () => api<FailoverDrillDetail>(`/v1/failover/drills/${selectedID}`),
    enabled: selectedID !== '',
    staleTime: 10_000,
  })

  const start = useMutation({
    mutationFn: (body: DrillForm) =>
      apiWrite<StartDrillResult>('/v1/failover/drills', 'POST', {
        mode: body.mode,
        primary_host: body.primary_host,
        primary_port: body.primary_port,
        standby_host: body.standby_host,
        standby_port: body.standby_port,
        slot_name: body.slot_name,
        primary_data_dir: body.primary_data_dir,
        standby_data_dir: body.standby_data_dir,
        acknowledge_warnings: body.acknowledge_warnings,
        observation: {
          primary_in_recovery: body.primary_in_recovery,
          standby_in_recovery: body.standby_in_recovery,
          standby_accepting: body.standby_accepting,
          replication_state: body.replication_state,
          replication_lag_ms: body.replication_lag_ms,
          replication_lag_bytes: body.replication_lag_bytes,
          slot_exists: body.slot_exists,
          slot_active: body.slot_active,
          slot_retained_bytes: body.slot_retained_bytes,
          checkpoint_age_ms: body.checkpoint_age_ms,
          primary_timeline: body.primary_timeline,
          standby_timeline: body.standby_timeline,
        },
      }),
    onSuccess: (result) => {
      setStartResult(result)
      setSelectedID(result.drill.id)
      setNotice(`เริ่ม drill ${result.drill.id} แล้ว · ${result.report.gate.reason}`)
      void qc.invalidateQueries({ queryKey: READINESS_KEY })
      void qc.invalidateQueries({ queryKey: DRILLS_KEY })
    },
  })

  const recordStep = useMutation({
    mutationFn: (input: { stepID: string; direction: 'forward' | 'reverse'; status: string; detail: string }) =>
      apiWrite<{ step: FailoverStep }>(`/v1/failover/drills/${selectedID}/steps`, 'POST', {
        step_id: input.stepID,
        direction: input.direction,
        status: input.status,
        detail: input.detail,
      }),
    onSuccess: (result) => {
      setNotice(`บันทึกขั้นตอน ${result.step.step_id} ครั้งที่ ${result.step.attempt} แล้ว`)
      setStepDetail('')
      void qc.invalidateQueries({ queryKey: ['failover-drill', selectedID] })
      void qc.invalidateQueries({ queryKey: DRILLS_KEY })
      void qc.invalidateQueries({ queryKey: READINESS_KEY })
    },
  })

  const validateDrill = useMutation({
    mutationFn: (body: typeof validation) =>
      apiWrite<ValidateDrillResult>(`/v1/failover/drills/${selectedID}/validate`, 'POST', body),
    onSuccess: (result) => {
      setNotice(`บันทึกผลตรวจหลังเลื่อนขั้นแล้ว · ${result.validation.reason}`)
      void qc.invalidateQueries({ queryKey: ['failover-drill', selectedID] })
      void qc.invalidateQueries({ queryKey: DRILLS_KEY })
      void qc.invalidateQueries({ queryKey: READINESS_KEY })
    },
  })

  const drills = drillsQ.data ?? []
  const readiness = readinessQ.data
  const detail = detailQ.data

  const stepOptions = detail
    ? detail.plan.steps.flatMap((step) => {
        const options = [{ value: `forward|${step.id}`, label: `${step.position}. ${step.title}` }]
        if (step.undo) {
          options.push({ value: `reverse|${step.undo.id}`, label: `${step.position}R. ${step.undo.title}` })
        }
        return options
      })
    : []

  function chooseStep(value: string) {
    setStepChoice(value)
    if (!value) return
    const [direction, stepID] = value.split('|')
    const step = detail?.plan.steps.find(
      (candidate) => candidate.id === stepID || candidate.undo?.id === stepID,
    )
    if (step && direction === 'reverse') {
      setStepStatus('rolled_back')
    } else {
      setStepStatus('done')
    }
  }

  function selectDrill(drill: FailoverDrill) {
    setSelectedID(drill.id)
    setStepChoice('')
    setValidation({
      promoted_host: drill.target.standby_host,
      promoted_port: drill.target.standby_port,
      promoted_in_recovery: false,
      writes_accepted: true,
      probe_value: 'promotion-ok',
      timeline: drill.primary_timeline + 1,
      old_primary_in_recovery: true,
      old_primary_streaming: true,
    })
  }

  const head = (
    <PageHead
      title="ซ้อมสลับเครื่องสำรอง (failover drill)"
      sub="ประเมินความพร้อมของคู่ primary/standby เปิดประตูตัดสินใจเลื่อนขั้นตามลำดับขั้นที่ย้อนกลับได้ และบันทึกหลักฐานก่อนและหลังการเลื่อนขั้น · ไม่มีคำสั่งใดถูกเรียกจากหน้านี้"
      actions={
        <button
          className="btn"
          disabled={readinessQ.isFetching || drillsQ.isFetching}
          onClick={() => {
            void readinessQ.refetch()
            void drillsQ.refetch()
          }}
        >
          {readinessQ.isFetching || drillsQ.isFetching ? 'กำลังตรวจสอบ…' : 'ตรวจสอบใหม่'}
        </button>
      }
    />
  )

  if (readinessQ.isLoading || drillsQ.isLoading) {
    return (
      <>
        {head}
        <div className="stack">
          <Skel h={110} />
          <Skel h={320} />
        </div>
      </>
    )
  }

  if (readinessQ.isError || !readiness) {
    return (
      <>
        {head}
        <Empty
          title="โหลดสถานะความพร้อมไม่ได้"
          body="ตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์ และตรวจว่าไมเกรชัน 0113 ถูกนำไปใช้กับฐานข้อมูลนี้แล้ว"
        />
      </>
    )
  }

  const last = readiness.last_drill

  return (
    <>
      {head}

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 16 }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      <div className="grid-4 stagger" style={{ marginBottom: 22 }}>
        <Stat
          label="ความพร้อมเลื่อนขั้น"
          value={readiness.ready ? 'พร้อม' : 'ยังไม่พร้อม'}
          foot={readiness.ready ? 'ผลตรวจล่าสุดอนุญาต' : 'ยังมีข้อขัดขวางหรือยังไม่ได้ซ้อม'}
        />
        <Stat
          label="drill ทั้งหมด"
          value={number(drills.length)}
          unit="ครั้ง"
          foot={last ? `ล่าสุด ${thaiDateTime(last.started_at)}` : 'ยังไม่มีประวัติ'}
        />
        <Stat
          label="ผลตรวจล่าสุด"
          value={last ? `${last.checks_passed}/${last.checks_total}` : '—'}
          foot={last ? `เตือน ${last.checks_warned} · ไม่ผ่าน ${last.checks_failed}` : 'ยังไม่มีผลตรวจ'}
        />
        <Stat
          label="ขั้นตอนที่บันทึก"
          value={number(readiness.steps_recorded)}
          unit="รายการ"
          foot={last ? `แผน ${last.steps_planned} ขั้น` : 'ยังไม่มีขั้นตอน'}
        />
      </div>

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div className="card-label">สถานะความพร้อม</div>
            <div className="dim" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.8 }}>{readiness.note}</div>
          </div>
          <span className={readiness.ready ? 'badge badge-closed' : 'badge badge-received'}>
            {readiness.ready ? 'พร้อม' : 'ยังไม่พร้อม'}
          </span>
        </div>
        {readiness.checks.length > 0 ? (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            {readiness.checks.map((check) => (
              <span key={check.id} className={checkClass(check.status)} title={check.detail}>
                {check.id} · {checkLabel(check.status)}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="card-label">เริ่ม drill ใหม่</div>
        <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
          กรอกค่าที่วัดได้จากคู่ primary/standby จริง · โหมดซ้อมแผนบันทึกผลได้แม้ถูกปฏิเสธ
          แต่โหมดดำเนินการจริงจะถูกปฏิเสธก่อนบันทึกเมื่อประตูตัดสินใจไม่อนุญาต
        </p>

        <div className="grid-2" style={{ marginTop: 16 }}>
          <div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="failover-mode">โหมด</label>
                <select
                  id="failover-mode"
                  className="select"
                  value={form.mode}
                  onChange={(event) => setForm({ ...form, mode: event.target.value as DrillForm['mode'] })}
                >
                  <option value="dry_run">ซ้อมแผน (dry run)</option>
                  <option value="execute">ดำเนินการจริง</option>
                </select>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="failover-slot">ชื่อ slot</label>
                <input
                  id="failover-slot"
                  className="input"
                  value={form.slot_name}
                  onChange={(event) => setForm({ ...form, slot_name: event.target.value })}
                />
              </div>
            </div>

            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 2 }}>
                <label htmlFor="failover-primary-host">primary host</label>
                <input
                  id="failover-primary-host"
                  className="input"
                  value={form.primary_host}
                  onChange={(event) => setForm({ ...form, primary_host: event.target.value })}
                />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="failover-primary-port">port</label>
                <input
                  id="failover-primary-port"
                  className="input"
                  type="number"
                  min={1}
                  max={65535}
                  value={form.primary_port}
                  onChange={(event) => setForm({ ...form, primary_port: Number(event.target.value) })}
                />
              </div>
            </div>

            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 2 }}>
                <label htmlFor="failover-standby-host">standby host</label>
                <input
                  id="failover-standby-host"
                  className="input"
                  value={form.standby_host}
                  onChange={(event) => setForm({ ...form, standby_host: event.target.value })}
                />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="failover-standby-port">port</label>
                <input
                  id="failover-standby-port"
                  className="input"
                  type="number"
                  min={1}
                  max={65535}
                  value={form.standby_port}
                  onChange={(event) => setForm({ ...form, standby_port: Number(event.target.value) })}
                />
              </div>
            </div>

            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="failover-primary-dir">primary data dir</label>
                <input
                  id="failover-primary-dir"
                  className="input"
                  value={form.primary_data_dir}
                  onChange={(event) => setForm({ ...form, primary_data_dir: event.target.value })}
                />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="failover-standby-dir">standby data dir</label>
                <input
                  id="failover-standby-dir"
                  className="input"
                  value={form.standby_data_dir}
                  onChange={(event) => setForm({ ...form, standby_data_dir: event.target.value })}
                />
              </div>
            </div>
          </div>

          <div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="failover-state">สถานะสตรีมมิ่ง</label>
                <select
                  id="failover-state"
                  className="select"
                  value={form.replication_state}
                  onChange={(event) => setForm({ ...form, replication_state: event.target.value })}
                >
                  <option value="streaming">streaming</option>
                  <option value="catchup">catchup</option>
                  <option value="startup">startup</option>
                  <option value="backup">backup</option>
                  <option value="stopped">stopped</option>
                  <option value="">ไม่ได้เชื่อมต่อ</option>
                </select>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="failover-lag">lag (ms)</label>
                <input
                  id="failover-lag"
                  className="input"
                  type="number"
                  min={0}
                  value={form.replication_lag_ms}
                  onChange={(event) => setForm({ ...form, replication_lag_ms: Number(event.target.value) })}
                />
              </div>
            </div>

            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="failover-lag-bytes">lag (bytes)</label>
                <input
                  id="failover-lag-bytes"
                  className="input"
                  type="number"
                  min={0}
                  value={form.replication_lag_bytes}
                  onChange={(event) => setForm({ ...form, replication_lag_bytes: Number(event.target.value) })}
                />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="failover-retained">WAL ที่ slot เก็บ (bytes)</label>
                <input
                  id="failover-retained"
                  className="input"
                  type="number"
                  min={0}
                  value={form.slot_retained_bytes}
                  onChange={(event) => setForm({ ...form, slot_retained_bytes: Number(event.target.value) })}
                />
              </div>
            </div>

            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="failover-checkpoint">checkpoint age (ms)</label>
                <input
                  id="failover-checkpoint"
                  className="input"
                  type="number"
                  min={0}
                  value={form.checkpoint_age_ms}
                  onChange={(event) => setForm({ ...form, checkpoint_age_ms: Number(event.target.value) })}
                />
              </div>
              <div className="field" style={{ flex: 0.5 }}>
                <label htmlFor="failover-primary-tl">timeline primary</label>
                <input
                  id="failover-primary-tl"
                  className="input"
                  type="number"
                  min={1}
                  value={form.primary_timeline}
                  onChange={(event) => setForm({ ...form, primary_timeline: Number(event.target.value) })}
                />
              </div>
              <div className="field" style={{ flex: 0.5 }}>
                <label htmlFor="failover-standby-tl">timeline standby</label>
                <input
                  id="failover-standby-tl"
                  className="input"
                  type="number"
                  min={1}
                  value={form.standby_timeline}
                  onChange={(event) => setForm({ ...form, standby_timeline: Number(event.target.value) })}
                />
              </div>
            </div>

            <div className="row" style={{ gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
              <label className="row" style={{ gap: 6, fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={form.primary_in_recovery}
                  onChange={(event) => setForm({ ...form, primary_in_recovery: event.target.checked })}
                />
                primary อยู่ใน recovery
              </label>
              <label className="row" style={{ gap: 6, fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={form.standby_in_recovery}
                  onChange={(event) => setForm({ ...form, standby_in_recovery: event.target.checked })}
                />
                standby อยู่ใน recovery
              </label>
              <label className="row" style={{ gap: 6, fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={form.standby_accepting}
                  onChange={(event) => setForm({ ...form, standby_accepting: event.target.checked })}
                />
                standby รับการเชื่อมต่ออ่าน
              </label>
              <label className="row" style={{ gap: 6, fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={form.slot_exists}
                  onChange={(event) => setForm({ ...form, slot_exists: event.target.checked })}
                />
                มี slot
              </label>
              <label className="row" style={{ gap: 6, fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={form.slot_active}
                  onChange={(event) => setForm({ ...form, slot_active: event.target.checked })}
                />
                slot มีผู้เชื่อมต่อ
              </label>
              <label className="row" style={{ gap: 6, fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={form.acknowledge_warnings}
                  onChange={(event) => setForm({ ...form, acknowledge_warnings: event.target.checked })}
                />
                ยืนยันคำเตือนแล้ว
              </label>
            </div>
          </div>
        </div>

        <div className="row" style={{ gap: 12, alignItems: 'center', marginTop: 16 }}>
          <button className="btn btn-primary" disabled={start.isPending} onClick={() => start.mutate(form)}>
            {start.isPending ? 'กำลังประเมิน…' : 'เริ่ม drill'}
          </button>
          <span className="dim" style={{ fontSize: 11.5 }}>
            คำขอนี้ต้องมี Idempotency-Key และจะไม่เรียกคำสั่งใดบนเครื่องปลายทาง
          </span>
        </div>
        {start.isError ? <div className="form-error">{errorText(start.error, 'เริ่ม drill ไม่สำเร็จ')}</div> : null}
      </div>

      {startResult ? (
        <div className="card card-pad" style={{ marginBottom: 22 }}>
          <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div className="card-label">ผลประเมินของ drill ที่เริ่มล่าสุด</div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                {thaiDateTime(startResult.report.observed_at)} · {modeLabel(startResult.drill.mode)}
              </div>
            </div>
            <span className={startResult.report.gate.allow ? 'badge badge-closed' : 'badge badge-received'}>
              {startResult.report.gate.allow ? 'อนุญาตให้เลื่อนขั้น' : 'ยังไม่อนุญาต'}
            </span>
          </div>
          <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, marginTop: 10 }}>
            {startResult.report.gate.reason}
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <span className="chip">ตรวจ {number(startResult.report.summary.total)} ข้อ</span>
            <span className="chip">ผ่าน {number(startResult.report.summary.passed)}</span>
            <span className="chip gold">เตือน {number(startResult.report.summary.warned)}</span>
            <span className="chip gold">ไม่ผ่าน {number(startResult.report.summary.failed)}</span>
            <span className="chip">lag {formatDuration(startResult.drill.replication_lag_ms)}</span>
            <span className="chip">checkpoint {formatDuration(startResult.drill.checkpoint_age_ms)}</span>
          </div>

          <div className="table-wrap" style={{ marginTop: 16 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 230 }}>รหัส</th>
                  <th>รายการ</th>
                  <th style={{ width: 100 }}>ผล</th>
                  <th>หลักฐาน</th>
                </tr>
              </thead>
              <tbody>
                {startResult.report.checks.map((check) => (
                  <tr key={check.id} style={{ cursor: 'default' }}>
                    <td className="mono" style={{ fontSize: 11 }}>{check.id}</td>
                    <td style={{ fontSize: 12.5 }}>{check.title}</td>
                    <td><span className={checkClass(check.status)}>{checkLabel(check.status)}</span></td>
                    <td className="dim" style={{ fontSize: 11.5 }}>
                      {check.detail}
                      {check.remediation ? <div style={{ marginTop: 4 }}>วิธีแก้: {check.remediation}</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card-label" style={{ marginTop: 22 }}>ลำดับขั้นที่ต้องทำ</div>
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 46 }}>#</th>
                  <th style={{ width: 110 }}>ช่วง</th>
                  <th style={{ width: 260 }}>ขั้นตอน</th>
                  <th>คำสั่ง / หมายเหตุ</th>
                  <th style={{ width: 150 }}>เงื่อนไข</th>
                </tr>
              </thead>
              <tbody>
                {startResult.plan.steps.map((step) => (
                  <tr key={step.id} style={{ cursor: 'default' }}>
                    <td className="mono">{step.position}</td>
                    <td className="dim" style={{ fontSize: 11.5 }}>{phaseLabel(step.phase)}</td>
                    <td style={{ fontSize: 12.5 }}>
                      {step.title}
                      <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>{step.detail}</div>
                    </td>
                    <td>
                      <div className="mono" style={{ fontSize: 10.5, lineHeight: 1.6, wordBreak: 'break-all' }}>
                        {step.command}
                      </div>
                      {step.undo ? (
                        <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                          ย้อนกลับ: {step.undo.title} — {step.undo.command}
                        </div>
                      ) : null}
                    </td>
                    <td className="dim" style={{ fontSize: 11 }}>
                      {step.irreversible ? 'ย้อนกลับไม่ได้' : 'ย้อนกลับได้'}
                      {step.requires_approval ? ' · ต้องอนุมัติ' : ''}
                      <div style={{ marginTop: 4 }}>timeout {number(step.timeout_seconds)} วินาที</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div className="card-label">ประวัติ drill</div>
          <span className="dim" style={{ fontSize: 11.5 }}>แสดงล่าสุด {number(drills.length)} รายการ</span>
        </div>
        <div className="table-wrap" style={{ marginTop: 16 }}>
          {drills.length === 0 ? (
            <div style={{ padding: 16 }}>
              <Empty
                title="ยังไม่มีประวัติ drill"
                body="เริ่ม drill แบบซ้อมแผนด้านบนเพื่อวัดความพร้อมและเก็บหลักฐานรอบแรก"
              />
            </div>
          ) : (
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 160 }}>เวลา</th>
                  <th style={{ width: 100 }}>โหมด</th>
                  <th style={{ width: 130 }}>สถานะ</th>
                  <th>คู่เป้าหมาย</th>
                  <th style={{ width: 110 }}>ผลตรวจ</th>
                  <th style={{ width: 150 }}>timeline</th>
                  <th style={{ width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {drills.map((drill) => (
                  <tr
                    key={drill.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => selectDrill(drill)}
                  >
                    <td className="dim" style={{ fontSize: 11 }}>{thaiDateTime(drill.started_at)}</td>
                    <td style={{ fontSize: 11.5 }}>{modeLabel(drill.mode)}</td>
                    <td><span className={statusClass(drill.status)}>{statusLabel(drill.status)}</span></td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      {drill.target.primary_host}:{drill.target.primary_port} → {drill.target.standby_host}:{drill.target.standby_port}
                    </td>
                    <td style={{ fontSize: 11.5 }}>
                      {number(drill.checks_passed)}/{number(drill.checks_total)}
                      {drill.checks_failed > 0 ? ` · ไม่ผ่าน ${number(drill.checks_failed)}` : ''}
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      {number(drill.primary_timeline)} → {drill.promoted_timeline ? number(drill.promoted_timeline) : '—'}
                      {drill.timeline_bumped ? ' · เพิ่มแล้ว' : ''}
                    </td>
                    <td>
                      <button className="btn" style={{ height: 28, padding: '0 10px', fontSize: 11 }}>
                        ดู
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selectedID ? (
        <div className="card card-pad">
          <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div className="card-label">รายละเอียด drill</div>
              <div className="mono dim" style={{ fontSize: 11, marginTop: 4 }}>{selectedID}</div>
            </div>
            {detail ? (
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <span className={statusClass(detail.drill.status)}>{statusLabel(detail.drill.status)}</span>
                <span className="chip">{modeLabel(detail.drill.mode)}</span>
                {detail.drill.validated ? (
                  <span className={detail.drill.validation_ok ? 'badge badge-closed' : 'badge badge-received'}>
                    {detail.drill.validation_ok ? 'ตรวจหลังเลื่อนขั้นผ่าน' : 'ตรวจหลังเลื่อนขั้นไม่ผ่าน'}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          {detailQ.isLoading ? (
            <div style={{ paddingTop: 16 }}><Skel h={180} /></div>
          ) : detailQ.isError || !detail ? (
            <div className="form-error" style={{ marginTop: 16 }}>
              {errorText(detailQ.error, 'โหลดรายละเอียด drill ไม่สำเร็จ')}
            </div>
          ) : (
            <>
              <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, marginTop: 10 }}>
                {detail.drill.decision_reason}
              </div>

              <div className="table-wrap" style={{ marginTop: 16 }}>
                {detail.steps.length === 0 ? (
                  <div style={{ padding: 16 }}>
                    <Empty
                      title="ยังไม่มีขั้นตอนที่บันทึก"
                      body="ใช้แบบฟอร์มด้านล่างเพื่อบันทึกผลของแต่ละขั้น หรือรัน scripts/failover-drill.sh แล้วบันทึกผลกลับมา"
                    />
                  </div>
                ) : (
                  <table className="table data">
                    <thead>
                      <tr>
                        <th style={{ width: 46 }}>#</th>
                        <th style={{ width: 80 }}>ทิศทาง</th>
                        <th style={{ width: 190 }}>รหัสขั้นตอน</th>
                        <th>ชื่อ</th>
                        <th style={{ width: 100 }}>ผล</th>
                        <th style={{ width: 60 }}>ครั้ง</th>
                        <th style={{ width: 160 }}>บันทึกเมื่อ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.steps.map((step) => (
                        <tr key={step.id} style={{ cursor: 'default' }}>
                          <td className="mono">{step.position}</td>
                          <td style={{ fontSize: 11.5 }}>{step.direction === 'reverse' ? 'ย้อนกลับ' : 'เดินหน้า'}</td>
                          <td className="mono" style={{ fontSize: 11 }}>{step.step_id}</td>
                          <td style={{ fontSize: 12 }}>
                            {step.title}
                            {step.detail ? <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>{step.detail}</div> : null}
                          </td>
                          <td style={{ fontSize: 11.5 }}>{stepStatusLabel(step.status)}</td>
                          <td className="mono">{step.attempt}</td>
                          <td className="dim" style={{ fontSize: 11 }}>{thaiDateTime(step.recorded_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {detail.drill.status === 'planned' || detail.drill.status === 'running' ? (
                <div className="grid-2" style={{ marginTop: 20 }}>
                  <div>
                    <div className="card-label">บันทึกผลขั้นตอน</div>
                    <div className="field">
                      <label htmlFor="failover-step">ขั้นตอน</label>
                      <select
                        id="failover-step"
                        className="select"
                        value={stepChoice}
                        onChange={(event) => chooseStep(event.target.value)}
                      >
                        <option value="">เลือกขั้นตอน</option>
                        {stepOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="row" style={{ gap: 10 }}>
                      <div className="field" style={{ flex: 1 }}>
                        <label htmlFor="failover-step-status">ผล</label>
                        <select
                          id="failover-step-status"
                          className="select"
                          value={stepStatus}
                          onChange={(event) => setStepStatus(event.target.value as FailoverStep['status'])}
                        >
                          <option value="done">สำเร็จ</option>
                          <option value="failed">ล้มเหลว</option>
                          <option value="skipped">ข้าม</option>
                          <option value="rolled_back">ย้อนกลับแล้ว</option>
                        </select>
                      </div>
                      <div className="field" style={{ flex: 2 }}>
                        <label htmlFor="failover-step-detail">รายละเอียด</label>
                        <input
                          id="failover-step-detail"
                          className="input"
                          value={stepDetail}
                          onChange={(event) => setStepDetail(event.target.value)}
                          placeholder="ข้อความจากคำสั่ง หรือเหตุผลที่ข้าม"
                        />
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      disabled={recordStep.isPending || stepChoice === ''}
                      onClick={() => {
                        const [direction, stepID] = stepChoice.split('|')
                        recordStep.mutate({
                          stepID,
                          direction: direction === 'reverse' ? 'reverse' : 'forward',
                          status: stepStatus,
                          detail: stepDetail,
                        })
                      }}
                    >
                      {recordStep.isPending ? 'กำลังบันทึก…' : 'บันทึกผลขั้นตอน'}
                    </button>
                    {recordStep.isError ? (
                      <div className="form-error">{errorText(recordStep.error, 'บันทึกผลขั้นตอนไม่สำเร็จ')}</div>
                    ) : null}
                  </div>

                  <div>
                    <div className="card-label">ผลตรวจหลังเลื่อนขั้น</div>
                    <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, margin: '8px 0 0' }}>
                      ใช้เมื่อเครื่อง standby ถูกเลื่อนขั้นแล้ว · timeline ต้องเป็นค่าเดิมบวกหนึ่ง และเครื่องเดิมต้องอยู่ในโหมด standby
                    </p>
                    <div className="row" style={{ gap: 10 }}>
                      <div className="field" style={{ flex: 2 }}>
                        <label htmlFor="failover-promoted-host">host ที่เลื่อนขั้น</label>
                        <input
                          id="failover-promoted-host"
                          className="input"
                          value={validation.promoted_host}
                          onChange={(event) => setValidation({ ...validation, promoted_host: event.target.value })}
                        />
                      </div>
                      <div className="field" style={{ flex: 1 }}>
                        <label htmlFor="failover-promoted-port">port</label>
                        <input
                          id="failover-promoted-port"
                          className="input"
                          type="number"
                          min={1}
                          max={65535}
                          value={validation.promoted_port}
                          onChange={(event) => setValidation({ ...validation, promoted_port: Number(event.target.value) })}
                        />
                      </div>
                      <div className="field" style={{ flex: 1 }}>
                        <label htmlFor="failover-promoted-timeline">timeline</label>
                        <input
                          id="failover-promoted-timeline"
                          className="input"
                          type="number"
                          min={1}
                          value={validation.timeline}
                          onChange={(event) => setValidation({ ...validation, timeline: Number(event.target.value) })}
                        />
                      </div>
                    </div>
                    <div className="row" style={{ gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
                      <label className="row" style={{ gap: 6, fontSize: 12 }}>
                        <input
                          type="checkbox"
                          checked={validation.writes_accepted}
                          onChange={(event) => setValidation({ ...validation, writes_accepted: event.target.checked })}
                        />
                        เขียนทดสอบสำเร็จ
                      </label>
                      <label className="row" style={{ gap: 6, fontSize: 12 }}>
                        <input
                          type="checkbox"
                          checked={validation.promoted_in_recovery}
                          onChange={(event) => setValidation({ ...validation, promoted_in_recovery: event.target.checked })}
                        />
                        เครื่องใหม่ยังอยู่ใน recovery
                      </label>
                      <label className="row" style={{ gap: 6, fontSize: 12 }}>
                        <input
                          type="checkbox"
                          checked={validation.old_primary_in_recovery}
                          onChange={(event) => setValidation({ ...validation, old_primary_in_recovery: event.target.checked })}
                        />
                        เครื่องเดิมอยู่ใน recovery
                      </label>
                      <label className="row" style={{ gap: 6, fontSize: 12 }}>
                        <input
                          type="checkbox"
                          checked={validation.old_primary_streaming}
                          onChange={(event) => setValidation({ ...validation, old_primary_streaming: event.target.checked })}
                        />
                        เครื่องเดิมสตรีมมิ่ง
                      </label>
                    </div>
                    <button
                      className="btn"
                      disabled={validateDrill.isPending}
                      onClick={() => validateDrill.mutate(validation)}
                      style={{ marginTop: 12 }}
                    >
                      {validateDrill.isPending ? 'กำลังตรวจสอบ…' : 'บันทึกผลตรวจหลังเลื่อนขั้น'}
                    </button>
                    {validateDrill.isError ? (
                      <div className="form-error">{errorText(validateDrill.error, 'บันทึกผลตรวจไม่สำเร็จ')}</div>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, marginTop: 16 }}>
                  drill นี้ปิดแล้ว · ผลตรวจหลังเลื่อนขั้น: {detail.drill.validation_reason || 'ไม่มี'}
                  {detail.drill.promoted_timeline ? ` · timeline ${number(detail.drill.primary_timeline)} → ${number(detail.drill.promoted_timeline)}` : ''}
                </div>
              )}
            </>
          )}
        </div>
      ) : null}
    </>
  )
}
